import { SignalingClient, ServerToClientMessage } from '../signaling';

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

interface PeerState {
  pc: RTCPeerConnection;
  stream: MediaStream;
  label: string;
  pendingCandidates: RTCIceCandidateInit[];
}

export interface ViewerCallbacks {
  onStreamAdded: (streamId: string, label: string, stream: MediaStream) => void;
  onStreamRemoved: (streamId: string) => void;
  onConnectionStateChange?: (streamId: string, state: 'connecting' | 'connected' | 'disconnected') => void;
}

export class ViewerConnection {
  private eventId: string;
  private signaling: SignalingClient;
  private peers: Map<string, PeerState> = new Map();
  private callbacks: ViewerCallbacks;

  constructor(eventId: string, callbacks: ViewerCallbacks) {
    this.eventId = eventId;
    this.callbacks = callbacks;
    console.log('[Viewer] Initializing viewer for event:', eventId);

    this.signaling = new SignalingClient(eventId, {
      onMessage: (msg) => this.handleSignalingMessage(msg),
      onConnected: () => {
        console.log('[Viewer] Signaling connected, sending join message');
        this.signaling.send({ type: 'join', role: 'viewer' });
      },
    });
  }

  connect(): void {
    console.log('[Viewer] Starting connection');
    this.signaling.connect();
  }

  disconnect(): void {
    this.peers.forEach((peer, streamId) => {
      peer.pc.close();
      this.callbacks.onStreamRemoved(streamId);
    });
    this.peers.clear();
    this.signaling.disconnect();
  }

  private handleSignalingMessage(message: ServerToClientMessage): void {
    switch (message.type) {
      case 'stream-added':
        this.handleStreamAdded(message.streamId, message.label);
        break;
      case 'stream-removed':
        this.handleStreamRemoved(message.streamId);
        break;
      case 'offer':
        this.handleOffer(message.streamId, message.sdp);
        break;
      case 'ice-candidate':
        this.handleIceCandidate(message.streamId, message.candidate);
        break;
    }
  }

  private handleStreamAdded(streamId: string, label: string): void {
    console.log('[Viewer] Stream added:', streamId, label);

    // Create a new peer connection for this stream
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const stream = new MediaStream();

    const peerState: PeerState = {
      pc,
      stream,
      label,
      pendingCandidates: [],
    };

    this.peers.set(streamId, peerState);

    // Handle incoming tracks
    pc.ontrack = (event) => {
      console.log('[Viewer] Track received for stream:', streamId, event.track.kind);
      event.streams[0].getTracks().forEach((track) => {
        stream.addTrack(track);
      });
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[Viewer] Sending ICE candidate for stream:', streamId);
        this.signaling.send({
          type: 'ice-candidate',
          candidate: event.candidate.toJSON(),
          streamId,
        });
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      console.log('[Viewer] Connection state changed for stream:', streamId, state);
      if (state === 'connected') {
        this.callbacks.onConnectionStateChange?.(streamId, 'connected');
      } else if (state === 'disconnected' || state === 'failed') {
        this.callbacks.onConnectionStateChange?.(streamId, 'disconnected');
      } else {
        this.callbacks.onConnectionStateChange?.(streamId, 'connecting');
      }
    };

    // Notify that stream was added
    this.callbacks.onStreamAdded(streamId, label, stream);
    this.callbacks.onConnectionStateChange?.(streamId, 'connecting');

    // Request offer from streamer
    console.log('[Viewer] Requesting offer from streamer:', streamId);
    this.signaling.send({ type: 'request-offer', streamId });
  }

  private handleStreamRemoved(streamId: string): void {
    const peer = this.peers.get(streamId);
    if (peer) {
      peer.pc.close();
      this.peers.delete(streamId);
      this.callbacks.onStreamRemoved(streamId);
    }
  }

  private async handleOffer(streamId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    console.log('[Viewer] Received offer from stream:', streamId);
    const peer = this.peers.get(streamId);
    if (!peer) {
      console.error('[Viewer] No peer found for stream:', streamId);
      return;
    }

    try {
      console.log('[Viewer] Setting remote description for stream:', streamId);
      await peer.pc.setRemoteDescription(new RTCSessionDescription(sdp));

      // Flush pending ICE candidates
      console.log('[Viewer] Flushing', peer.pendingCandidates.length, 'pending ICE candidates');
      for (const candidate of peer.pendingCandidates) {
        await peer.pc.addIceCandidate(new RTCIceCandidate(candidate));
      }
      peer.pendingCandidates = [];

      // Create and send answer
      console.log('[Viewer] Creating answer for stream:', streamId);
      const answer = await peer.pc.createAnswer();
      await peer.pc.setLocalDescription(answer);

      console.log('[Viewer] Sending answer for stream:', streamId);
      this.signaling.send({
        type: 'answer',
        sdp: peer.pc.localDescription!,
        streamId,
      });
    } catch (error) {
      console.error('[Viewer] Failed to handle offer:', error);
    }
  }

  private async handleIceCandidate(streamId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const peer = this.peers.get(streamId);
    if (!peer) return;

    try {
      if (peer.pc.remoteDescription) {
        await peer.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        // Buffer candidate until remote description is set
        peer.pendingCandidates.push(candidate);
      }
    } catch (error) {
      console.error('Failed to add ICE candidate:', error);
    }
  }
}
